import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, User, Lock, CreditCard, History, LogOut, Check, Loader2, Shield, Activity, Save, Info,
  AlertTriangle, Gift, ArrowLeft, Wallet, RefreshCw, Trash2, Plus
} from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';
import { API_BASE_URL } from '../config/api';
import { useSubscription } from '../context/SubscriptionContext';
import { useCexAccount } from '../context/CexAccountContext';
import { auth } from '../services/firebase';
import { EmailAuthProvider, linkWithCredential, updatePassword } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_WALLET_NETWORKS } from '../config/wallet';
import AddWalletModal, { WalletAccountData } from './AddWalletModal';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'profile' | 'security' | 'subscription' | 'history' | 'wallets';

interface LogEntry {
  id: string;
  action: string;
  time: string;
  ip: string;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { userName, setUserName, logout } = usePortfolio();
  const { effectivePlan, subscription, refresh: refreshSubscription } = useSubscription();
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  // Cancellation Flow States
  const [cancelFlowStep, setCancelFlowStep] = useState<'idle' | 'reason' | 'offer' | 'confirm_skip' | 'success' | 'waiting_payment' | 'payment_success'>('idle');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string>('');

  useEffect(() => {
    setCancelFlowStep('idle');
    setSelectedReason('');
    setCancelError('');
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) {
      setCancelFlowStep('idle');
      setSelectedReason('');
      setCancelError('');
    }
  }, [isOpen]);

  // Polling for payment status when in 'waiting_payment' state
  useEffect(() => {
    if (cancelFlowStep !== 'waiting_payment') return;

    const intervalId = setInterval(async () => {
      if (refreshSubscription) {
        await refreshSubscription();
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [cancelFlowStep, refreshSubscription]);

  // Monitor subscription changes to detect payment success
  useEffect(() => {
    if (cancelFlowStep === 'waiting_payment' && subscription) {
      if (subscription.status === 'active') {
        setCancelFlowStep('payment_success');
      }
    }
  }, [subscription, cancelFlowStep]);
  
  // Profile Form States
  const [name, setName] = useState(userName);
  const [avatarUrl, setAvatarUrl] = useState(() => localStorage.getItem('trader_avatar_url') || '');
  const [isSavingName, setIsSavingName] = useState(false);

  // Wallet tab state
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [addNetworkAddress, setAddNetworkAddress] = useState<string | undefined>(undefined);
  const [addNetworkTracked, setAddNetworkTracked] = useState<string[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [referralCodeUsed, setReferralCodeUsed] = useState<string | null>(null);
  
  // Form fields parsed from user_form_data in localStorage
  const [formData, setFormData] = useState<any>(null);

  // Security Form States
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [securitySuccess, setSecuritySuccess] = useState('');
  const [securityError, setSecurityError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);

  // 2FA Setup States
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCodeUri, setQrCodeUri] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isActivating2FA, setIsActivating2FA] = useState(false);

  // History Log States
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const getOfferTranslationKey = (reason: string) => {
    switch (reason) {
      case 'technical_issues': return 'settings.offerTechnical';
      case 'budget': return 'settings.offerBudget';
      case 'switching': return 'settings.offerSwitching';
      case 'break': return 'settings.offerBreak';
      case 'missing_features': return 'settings.offerMissingFeatures';
      default: return 'settings.offerOther';
    }
  };

  const handleApplyDiscount = async () => {
    if (!selectedReason) return;
    setIsCancelLoading(true);
    setCancelError('');
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/subscription/create-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          plan: subscription?.plan || 'premium',
          cycle: 'monthly',
          discount_reason: selectedReason,
        }),
      });

      const data = await response.json();
      if (response.ok || response.status === 409) {
        if (data.payment_url) {
          window.open(data.payment_url, '_blank');
          if (response.ok && refreshSubscription) {
            await refreshSubscription();
          }
          setCancelFlowStep('waiting_payment');
        } else {
          setCancelError(t('settings.errorInit2FA')); // fallback error key
        }
      } else {
        setCancelError(data.message || 'Failed to apply offer.');
      }
    } catch (err: any) {
      console.error(err);
      setCancelError(err.message || 'An error occurred.');
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelLoading(true);
    setCancelError('');
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (response.ok) {
        setCancelFlowStep('success');
        if (refreshSubscription) {
          await refreshSubscription();
        }
      } else {
        setCancelError(data.message || 'Failed to cancel subscription.');
      }
    } catch (err: any) {
      console.error(err);
      setCancelError(err.message || 'An error occurred.');
    } finally {
      setIsCancelLoading(false);
    }
  };

  useEffect(() => {
    setName(userName);
  }, [userName]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const response = await fetch(`${API_BASE_URL}/api/user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        if (response.ok) {
          const userData = await response.json();
          if (userData.name) {
            setName(userData.name);
            setUserName(userData.name);
          }
          if (userData.avatar_url) {
            setAvatarUrl(userData.avatar_url);
            localStorage.setItem('trader_avatar_url', userData.avatar_url);
            window.dispatchEvent(new Event('avatar_updated'));
          }
          setTwoFactor(!!userData.two_factor_enabled);
          setReferralCodeUsed(userData.referral_code_used || userData.referral_code || null);
        }
      } catch (err) {
        console.error('Failed to fetch user settings:', err);
      }
    };

    fetchUserData();
  }, [isOpen, setUserName]);

  useEffect(() => {
    const savedForm = localStorage.getItem('user_form_data');
    if (savedForm) {
      try {
        setFormData(JSON.parse(savedForm));
      } catch (e) {
        console.error(e);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Load or generate activity logs
    const savedLogs = localStorage.getItem('activity_logs');
    if (savedLogs) {
      try {
        setLogs(JSON.parse(savedLogs));
      } catch (e) {
        setLogs(generateMockLogs());
      }
    } else {
      const mock = generateMockLogs();
      localStorage.setItem('activity_logs', JSON.stringify(mock));
      setLogs(mock);
    }
  }, [isOpen]);

  const generateMockLogs = (): LogEntry[] => {
    const now = new Date();
    const currentLang = i18n.language || 'id';
    const timeLocale = currentLang === 'id' ? 'id-ID' : 'en-US';
    return [
      {
        id: '1',
        action: 'settings.logSuccessAddTrade',
        time: new Date(now.getTime() - 1000 * 60 * 15).toLocaleString(timeLocale), // 15 mins ago
        ip: '182.253.140.231'
      },
      {
        id: '2',
        action: 'settings.logAutoSync',
        time: new Date(now.getTime() - 1000 * 60 * 60 * 2).toLocaleString(timeLocale), // 2 hours ago
        ip: '182.253.140.231'
      },
      {
        id: '3',
        action: 'settings.logConnectBybit',
        time: new Date(now.getTime() - 1000 * 60 * 60 * 24).toLocaleString(timeLocale), // 1 day ago
        ip: '182.253.140.231'
      },
      {
        id: '4',
        action: 'settings.logLoginSuccess',
        time: new Date(now.getTime() - 1000 * 60 * 60 * 24.5).toLocaleString(timeLocale), // 1 day ago
        ip: '182.253.140.231'
      },
      {
        id: '5',
        action: 'settings.logOnboardingComplete',
        time: new Date(now.getTime() - 1000 * 60 * 60 * 24 * 3).toLocaleString(timeLocale), // 3 days ago
        ip: '114.124.210.87'
      }
    ];
  };

  const addLog = (action: string) => {
    const currentLang = i18n.language || 'id';
    const timeLocale = currentLang === 'id' ? 'id-ID' : 'en-US';
    const newEntry: LogEntry = {
      id: Math.random().toString(36).slice(2),
      action,
      time: new Date().toLocaleString(timeLocale),
      ip: '182.253.140.231'
    };
    const updated = [newEntry, ...logs];
    setLogs(updated);
    localStorage.setItem('activity_logs', JSON.stringify(updated));
  };

  const uploadFile = async (file: File): Promise<string> => {
    const token = localStorage.getItem('auth_token');
    const fd = new FormData();
    fd.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: fd,
    });
    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.message || 'Upload failed');
    }
    const data = await response.json();
    return data.url;
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSavingName(true);
    setSaveSuccess(false);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          avatar_url: avatarUrl,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || t('settings.errorUploadAvatar'));
      }

      const data = await response.json();
      setUserName(name.trim());
      if (data.user && data.user.avatar_url) {
        setAvatarUrl(data.user.avatar_url);
        localStorage.setItem('trader_avatar_url', data.user.avatar_url);
      }
      setSaveSuccess(true);
      addLog(t('settings.logChangeName', { name: name.trim() }));
      
      // Update user_form_data in localStorage
      const savedForm = localStorage.getItem('user_form_data');
      if (savedForm) {
        const parsed = JSON.parse(savedForm);
        parsed.name = name.trim();
        localStorage.setItem('user_form_data', JSON.stringify(parsed));
        setFormData(parsed);
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || t('settings.errorUploadAvatar'));
    } finally {
      setIsSavingName(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSecurityError('');
    setSecuritySuccess('');

    if (!newPassword || !confirmPassword) {
      setSecurityError(t('settings.errorReqPassword'));
      return;
    }

    if (newPassword.length < 6) {
      setSecurityError(t('settings.errorMinPassword'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setSecurityError(t('settings.errorMismatchPassword'));
      return;
    }

    setIsChangingPassword(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error(t('settings.errorFirebaseSession'));
      }

      // Check if user has password provider linked
      const hasPasswordProvider = currentUser.providerData.some(
        (provider) => provider.providerId === 'password'
      );

      if (hasPasswordProvider) {
        // Update password for existing email/password user
        await updatePassword(currentUser, newPassword);
      } else {
        // Link password credential for Google/OAuth user
        if (!currentUser.email) {
          throw new Error(t('settings.errorNoEmail'));
        }
        const credential = EmailAuthProvider.credential(currentUser.email, newPassword);
        await linkWithCredential(currentUser, credential);
      }

      // Update password in Go backend
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/user/password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        setSecuritySuccess(t('settings.firebaseConnectedHelp'));
        addLog(t('settings.logSecurityPassword'));
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setSecurityError(data.message || t('settings.errorSavePassword'));
      }
    } catch (err: any) {
      console.error('Change password failed:', err);
      let errorMsg = err.message || t('settings.errorSavePassword');
      if (err.code === 'auth/requires-recent-login') {
        errorMsg = t('settings.errorExpiredSession');
      } else if (err.code === 'auth/credential-already-in-use') {
        errorMsg = t('settings.errorEmailInUse');
      }
      setSecurityError(errorMsg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handle2FAToggle = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    if (twoFactor) {
      // Deactivate flow
      const code = prompt(t('settings.promptDeactivate2FA'));
      if (!code) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/user/two-factor/deactivate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ code })
        });
        const data = await response.json();
        if (response.ok) {
          setTwoFactor(false);
          addLog(t('settings.logDeactivate2FA'));
          alert(t('settings.deactivate2FASuccess'));
        } else {
          alert(data.message || t('settings.errorDeactivate2FA'));
        }
      } catch (err) {
        console.error(err);
        alert(t('settings.errorDeactivate2FA'));
      }
    } else {
      // Activate setup flow
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/two-factor/setup`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setSecretKey(data.secret);
          setQrCodeUri(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.otpauth_url)}`);
          setOtpError('');
          setOtpCode('');
          setShow2FASetup(true);
        } else {
          const data = await response.json();
          alert(data.message || t('settings.errorInit2FA'));
        }
      } catch (err) {
        console.error(err);
        alert(t('settings.errorStart2FA'));
      }
    }
  };

  const handleConfirm2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setOtpError(t('settings.errorOtpLength'));
      return;
    }

    setIsActivating2FA(true);
    setOtpError('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/user/two-factor/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ code: otpCode })
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFactor(true);
        setShow2FASetup(false);
        addLog(t('settings.logActivate2FA'));
        alert(t('settings.activate2FASuccess'));
      } else {
        setOtpError(data.message || t('settings.errorVerifyOtp'));
      }
    } catch (err) {
      console.error(err);
      setOtpError(t('settings.errorVerifyOtp'));
    } finally {
      setIsActivating2FA(false);
    }
  };

  const getPlanName = () => {
    if (effectivePlan === 'premium') return '👑 Premium';
    if (effectivePlan === 'pro') return '⚡ Pro';
    return '🌱 Free Trial';
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={onClose} 
            className="fixed inset-0 bg-black/85 backdrop-blur-md z-[100]" 
          />

          {/* Modal Container */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.97, y: 15 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 15 }}
            transition={{ type: 'spring', damping: 28, stiffness: 350 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6"
          >
            <div data-lenis-prevent className="bg-[#0D0D0D] border border-neutral-900 rounded-3xl w-full max-w-full sm:max-w-3xl lg:max-w-4xl h-[85vh] sm:h-[80vh] lg:h-[85vh] max-h-[900px] overflow-hidden flex flex-col sm:flex-row shadow-2xl">
              
              {/* Left Navigation Panel */}
              <div className="w-full sm:w-64 bg-[#090909] border-b sm:border-b-0 sm:border-r border-neutral-900 p-4 sm:p-6 flex flex-col shrink-0 overflow-y-auto sm:overflow-y-visible">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-8 h-8 rounded-lg bg-red-600/10 border border-red-600/30 flex items-center justify-center">
                    <Shield size={16} className="text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white tracking-tight uppercase">{t('settings.title')}</h3>
                    <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">{t('settings.controlPanel')}</p>
                  </div>
                </div>

                <nav className="flex flex-row sm:flex-col gap-1 sm:gap-1 overflow-x-auto sm:overflow-x-visible pb-3 sm:pb-0 scrollbar-hide px-1 sm:px-0">
                  <TabButton 
                    active={activeTab === 'profile'} 
                    onClick={() => setActiveTab('profile')}
                    icon={<User size={16} />}
                    label={t('settings.profile')}
                  />
                  <TabButton 
                    active={activeTab === 'security'} 
                    onClick={() => setActiveTab('security')}
                    icon={<Lock size={16} />}
                    label={t('settings.security')}
                  />
                  <TabButton 
                    active={activeTab === 'subscription'} 
                    onClick={() => setActiveTab('subscription')}
                    icon={<CreditCard size={16} />}
                    label={t('settings.subscription')}
                  />
                  <TabButton 
                    active={activeTab === 'history'} 
                    onClick={() => setActiveTab('history')}
                    icon={<History size={16} />}
                    label={t('settings.historyLog')}
                  />
                  <TabButton 
                    active={activeTab === 'wallets'} 
                    onClick={() => setActiveTab('wallets')}
                    icon={<Wallet size={16} />}
                    label={t('settings.wallets')}
                  />
                </nav>

                <div className="mt-auto hidden sm:block">
                  <button 
                    onClick={logout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-500 hover:text-red-500 hover:bg-red-500/5 transition-all text-xs font-bold font-mono tracking-wider uppercase border border-transparent hover:border-red-500/20"
                  >
                    <LogOut size={16} />
                    {t('topbar.logout')}
                  </button>
                </div>
              </div>

              {/* Right Content Panel */}
              <div className="flex-1 flex flex-col h-full overflow-hidden bg-[#0D0D0D] min-h-0">
                
                {/* Header */}
                <div className="px-6 sm:px-8 py-4 sm:py-5 border-b border-neutral-900/60 flex justify-between items-center bg-[#0D0D0D] shrink-0">
                  <h2 className="text-lg font-black text-white tracking-tight capitalize flex items-center gap-2">
                    {activeTab === 'profile' && t('settings.profileDetails')}
                    {activeTab === 'security' && t('settings.securityAccess')}
                    {activeTab === 'subscription' && t('settings.subscriptionPlans')}
                    {activeTab === 'history' && t('settings.activityLogTitle')}
                    {activeTab === 'wallets' && t('settings.manageNetworks')}
                  </h2>
                  <button 
                    onClick={onClose} 
                    className="p-1.5 hover:bg-neutral-900 border border-neutral-900 rounded-xl transition-all text-neutral-500 hover:text-white"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Body Content */}
                <div 
                  className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 scrollbar-thin"
                  style={{
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#525252 #171717'
                  }}
                >
                  <AnimatePresence mode="wait">
                    
                    {/* TAB: PROFILE */}
                    {activeTab === 'profile' && (
                      <motion.div 
                        key="profile" 
                        initial={{ opacity: 0, x: 10 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-6"
                      >
                        <form onSubmit={handleSaveProfile} className="space-y-5">
                          {/* Profile Photo Upload and Selection presets */}
                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-5 space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{t('settings.profilePhoto')}</h4>
                            <div className="flex flex-col sm:flex-row items-center gap-6">
                              <div className="relative group shrink-0">
                                {avatarUrl ? (
                                  <img src={avatarUrl} alt="Profile" className="w-20 h-20 rounded-2xl object-cover border-2 border-red-500/30 shadow-lg" />
                                ) : (
                                  <div className="w-20 h-20 bg-[#A3E2FF] text-[#0A0A0A] rounded-2xl flex items-center justify-center font-bold text-3xl uppercase shadow-lg select-none">
                                    {userName ? userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0,2) : 'U'}
                                  </div>
                                )}
                                {avatarUrl && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      localStorage.removeItem('trader_avatar_url');
                                      setAvatarUrl('');
                                      window.dispatchEvent(new Event('avatar_updated'));
                                      addLog(t('settings.logRemoveAvatar'));
                                    }}
                                    className="absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-red-600 hover:bg-red-500 rounded-full text-white border-2 border-[#0D0D0D] shadow-md outline-none transition-all active:scale-95"
                                  >
                                    <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
                                  </button>
                                )}
                              </div>

                              <div className="flex-1 space-y-3">
                                <p className="text-[10px] text-neutral-500 font-medium">{t('settings.photoHelp')}</p>
                                
                                {/* Presets list */}
                                <div className="flex gap-2 flex-wrap">
                                  {[
                                    { name: 'Bull', url: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=120&h=120&q=80' },
                                    { name: 'Bear', url: 'https://images.unsplash.com/photo-1530595467537-0b5996c41f2d?auto=format&fit=crop&w=120&h=120&q=80' },
                                    { name: 'Bitcoin', url: 'https://images.unsplash.com/photo-1516245834210-c4c142787335?auto=format&fit=crop&w=120&h=120&q=80' },
                                    { name: 'Future Trader', url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=120&h=120&q=80' },
                                  ].map(preset => (
                                    <button
                                      key={preset.name}
                                      type="button"
                                      onClick={() => {
                                        localStorage.setItem('trader_avatar_url', preset.url);
                                        setAvatarUrl(preset.url);
                                        window.dispatchEvent(new Event('avatar_updated'));
                                        addLog(t('settings.logChangeAvatarPreset', { preset: preset.name }));
                                      }}
                                      className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all outline-none ${
                                        avatarUrl === preset.url ? 'border-red-500/50 text-red-400 bg-red-950/15' : 'border-neutral-800 text-neutral-400 hover:text-white'
                                      }`}
                                    >
                                      {preset.name}
                                    </button>
                                  ))}
                                </div>

                                <div className="flex items-center gap-3">
                                  <label className="px-4 py-2 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 hover:text-white rounded-xl text-[10px] font-black text-neutral-400 uppercase tracking-wider transition-all cursor-pointer shadow-md inline-block">
                                    {isUploadingAvatar ? t('settings.uploading') : t('settings.uploadCustom')}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      disabled={isUploadingAvatar}
                                      className="hidden"
                                      onChange={async e => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          if (file.size > 2 * 1024 * 1024) {
                                            alert(t('settings.alertFileSize'));
                                            return;
                                          }
                                          setIsUploadingAvatar(true);
                                          try {
                                            const url = await uploadFile(file);
                                            setAvatarUrl(url);
                                            localStorage.setItem('trader_avatar_url', url);
                                            window.dispatchEvent(new Event('avatar_updated'));
                                            addLog(t('settings.logUploadAvatarCustom'));
                                          } catch (err: any) {
                                            alert(err.message || t('settings.errorUploadAvatar'));
                                          } finally {
                                            setIsUploadingAvatar(false);
                                          }
                                        }
                                      }}
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-5 space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{t('settings.onboardingTour')}</h4>
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                              <div className="space-y-1">
                                <p className="text-xs font-bold text-white">{t('settings.restartTour')}</p>
                                <p className="text-[10px] text-neutral-500 font-medium">{t('settings.restartTourHelp')}</p>
                              </div>
                              <div className="flex flex-wrap gap-2 shrink-0">
                                {[
                                  { label: 'Dashboard', key: 'dashboard' },
                                  { label: 'Trades', key: 'trades' },
                                  { label: 'Reports', key: 'reports' },
                                  { label: 'Notebook', key: 'notebook' },
                                ].map(tour => (
                                  <button
                                    type="button"
                                    key={tour.key}
                                    onClick={() => {
                                      // Remove completed key
                                      localStorage.removeItem(`completed_tour_${tour.key}`);
                                      // Dispatch global restart event
                                      window.dispatchEvent(new CustomEvent('restart_tour', { detail: tour.key }));
                                      addLog(t('settings.logRestartTour', { tour: tour.label }));
                                      onClose();
                                    }}
                                    className="px-3 py-1.5 bg-neutral-900 border border-neutral-800 hover:border-red-600 rounded-xl text-[10px] font-black text-neutral-400 hover:text-white uppercase tracking-wider transition-all shadow active:scale-[0.98] outline-none"
                                  >
                                    {tour.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-5 space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{t('settings.basicInfo')}</h4>
                            
                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">{t('settings.fullName')}</label>
                              <div className="flex gap-3">
                                <input 
                                  type="text" 
                                  value={name}
                                  onChange={e => setName(e.target.value)}
                                  className="flex-1 bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-sm text-white focus:border-red-500/60 transition-all font-bold outline-none"
                                />
                                <button
                                  type="submit"
                                  disabled={isSavingName || name.trim() === userName}
                                  className="px-5 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 border border-transparent disabled:border-neutral-800 disabled:text-neutral-600 rounded-xl text-xs font-black text-white uppercase transition-all flex items-center gap-2 shadow-lg active:scale-[0.98] outline-none"
                                >
                                  {isSavingName ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <Save size={14} />
                                  )}
                                  {t('settings.save')}
                                </button>
                              </div>
                              {saveSuccess && (
                                <p className="text-[10px] text-green-500 font-bold mt-1.5 flex items-center gap-1">
                                  <Check size={10} /> {t('settings.savedSuccess')}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">{t('settings.emailAddress')}</label>
                              <input 
                                type="email" 
                                value={formData?.email || 'user@example.com'} 
                                disabled
                                className="w-full bg-neutral-950/60 border border-neutral-900 rounded-xl px-4 py-3 text-sm text-neutral-500 font-mono focus:outline-none cursor-not-allowed"
                              />
                              <p className="text-[9px] text-neutral-600 mt-1.5">{t('settings.emailHelp')}</p>
                            </div>
                          </div>

                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-5 space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{t('settings.tradingProfile')}</h4>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1">{t('settings.traderType')}</label>
                                <div className="text-xs font-bold text-white capitalize bg-neutral-900/60 border border-neutral-900 px-3.5 py-2.5 rounded-xl">
                                  {formData?.traderType || 'Scalper / Day Trader'}
                                </div>
                              </div>
                              <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1">{t('settings.primaryGoal')}</label>
                                <div className="text-xs font-bold text-white capitalize bg-neutral-900/60 border border-neutral-900 px-3.5 py-2.5 rounded-xl">
                                  {formData?.goal || 'Grow Portfolio'}
                                </div>
                              </div>
                              <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1">{t('settings.gender')}</label>
                                <div className="text-xs font-bold text-white capitalize bg-neutral-900/60 border border-neutral-900 px-3.5 py-2.5 rounded-xl">
                                  {formData?.gender ? (formData.gender === 'Pria' || formData.gender === 'Male' ? t('settings.genderMale') : t('settings.genderFemale')) : t('settings.genderMale')}
                                </div>
                              </div>
                              <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1">{t('settings.birthYear')}</label>
                                <div className="text-xs font-bold text-white bg-neutral-900/60 border border-neutral-900 px-3.5 py-2.5 rounded-xl">
                                  {formData?.birthYear || '1998'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </form>
                      </motion.div>
                    )}

                    {/* TAB: SECURITY */}
                    {activeTab === 'security' && (
                      <motion.div 
                        key="security" 
                        initial={{ opacity: 0, x: 10 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-6"
                      >
                        <form onSubmit={handleChangePassword} className="space-y-5">
                          {/* Google / Firebase sign in message */}
                          <div className="bg-blue-950/20 border border-blue-900/40 rounded-2xl p-4 flex gap-3 text-xs text-blue-400 font-semibold leading-relaxed">
                            <Info size={16} className="shrink-0 mt-0.5" />
                            <div>
                              <p className="font-bold text-white mb-0.5">{t('settings.firebaseConnected')}</p>
                              <p className="text-neutral-400 font-medium">{t('settings.firebaseConnectedHelp')}</p>
                            </div>
                          </div>

                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-5 space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-neutral-400">{t('settings.setLoginPassword')}</h4>
                            
                            {securityError && (
                              <div className="p-3 bg-red-950/20 border border-red-950/50 rounded-xl text-xs text-red-400 font-semibold">
                                {securityError}
                              </div>
                            )}

                            {securitySuccess && (
                              <div className="p-3 bg-green-950/20 border border-green-950/50 rounded-xl text-xs text-green-400 font-semibold flex items-center gap-1.5">
                                <Check size={14} />
                                {securitySuccess}
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">{t('settings.newPassword')}</label>
                                <input 
                                  type="password" 
                                  value={newPassword}
                                  onChange={e => setNewPassword(e.target.value)}
                                  className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-sm text-white focus:border-red-500/60 transition-all font-mono outline-none"
                                  placeholder={t('settings.minChar')}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">{t('settings.confirmNewPassword')}</label>
                                <input 
                                  type="password" 
                                  value={confirmPassword}
                                  onChange={e => setConfirmPassword(e.target.value)}
                                  className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-sm text-white focus:border-red-500/60 transition-all font-mono outline-none"
                                  placeholder={t('settings.confirmPasswordPlaceholder')}
                                />
                              </div>
                            </div>

                            <button
                              type="submit"
                              disabled={isChangingPassword}
                              className="px-6 py-3 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 border border-transparent disabled:border-neutral-800 disabled:text-neutral-600 rounded-xl text-xs font-black text-white uppercase transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-900/10 active:scale-[0.98] w-fit outline-none"
                            >
                              {isChangingPassword ? (
                                <><Loader2 size={14} className="animate-spin" /> {t('settings.savingPassword')}</>
                              ) : (
                                t('settings.setUpdatePassword')
                              )}
                            </button>
                          </div>

                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-5 space-y-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-sm font-black text-white">{t('settings.twoFactorTitle')}</h4>
                                <p className="text-[10px] text-neutral-500 mt-0.5">{t('settings.twoFactorHelp')}</p>
                              </div>
                              <button 
                                type="button"
                                onClick={handle2FAToggle}
                                className={`w-12 h-6 rounded-full relative transition-all border outline-none ${
                                  (twoFactor || show2FASetup) 
                                    ? 'bg-red-600 border-red-500/50' 
                                    : 'bg-neutral-900 border-neutral-800'
                                }`}
                              >
                                <div className={`w-4 h-4 rounded-md absolute top-1/2 -translate-y-1/2 transition-all ${
                                  (twoFactor || show2FASetup) 
                                    ? 'left-7 bg-white' 
                                    : 'left-1 bg-neutral-600'
                                }`} />
                              </button>
                            </div>

                            {show2FASetup && (
                              <div className="mt-4 pt-4 border-t border-neutral-800 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="flex flex-col md:flex-row gap-5 items-center">
                                  <div className="p-3 bg-white rounded-xl shrink-0">
                                    <img src={qrCodeUri} alt="QR Code 2FA" className="w-40 h-40" />
                                  </div>
                                  <div className="flex-1 space-y-2.5">
                                    <p className="text-xs text-neutral-300 font-bold">{t('settings.scanQr')}</p>
                                    <p className="text-[10px] text-neutral-500 font-medium">{t('settings.scanQrHelp')}</p>
                                    
                                    <div className="pt-1">
                                      <p className="text-[9px] font-black uppercase text-neutral-500 tracking-wider">{t('settings.secretKey')}</p>
                                      <code className="text-xs text-red-400 font-mono font-bold select-all bg-neutral-900/60 px-2 py-1.5 rounded-lg border border-neutral-800 block w-fit mt-1">{secretKey}</code>
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-2 pt-2 border-t border-neutral-800/50">
                                  <p className="text-xs text-neutral-300 font-bold">{t('settings.enterOtp')}</p>
                                  
                                  {otpError && (
                                    <p className="text-[10px] text-red-500 font-bold">{otpError}</p>
                                  )}

                                  <div className="flex gap-3">
                                    <input
                                      type="text"
                                      maxLength={6}
                                      value={otpCode}
                                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                      placeholder={t('settings.otpPlaceholder')}
                                      className="bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:border-red-500/60 transition-all font-bold outline-none tracking-widest w-40 text-center"
                                    />
                                    <button
                                      type="button"
                                      onClick={handleConfirm2FA}
                                      disabled={isActivating2FA || otpCode.length !== 6}
                                      className="px-5 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 border border-transparent disabled:border-neutral-800 disabled:text-neutral-600 rounded-xl text-xs font-black text-white uppercase transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-900/10 active:scale-[0.98] outline-none"
                                    >
                                      {isActivating2FA ? t('settings.verifying') : t('settings.verifyActivate')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setShow2FASetup(false)}
                                      className="px-4 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-xl text-xs font-black text-neutral-400 hover:text-white uppercase transition-all outline-none"
                                    >
                                      {t('common.cancel')}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </form>
                      </motion.div>
                    )}

                    {/* TAB: SUBSCRIPTION */}
                    {activeTab === 'subscription' && (
                      <motion.div 
                        key="subscription" 
                        initial={{ opacity: 0, x: 10 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-6"
                      >
                        {cancelFlowStep === 'idle' && (
                          <>
                            <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-6 relative overflow-hidden">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-red-600 rounded-full blur-[70px] opacity-10" />
                              
                              <div className="flex items-center justify-between mb-6">
                                <div>
                                  <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500">{t('settings.currentPlan')}</p>
                                  <h3 className="text-2xl font-black text-white mt-1 tracking-tight">{getPlanName()}</h3>
                                  <p className="text-xs text-neutral-400 mt-1">
                                    Status:{' '}
                                    <span className={`${subscription?.status === 'cancelled' ? 'text-neutral-500' : 'text-green-500'} font-bold uppercase`}>
                                      {subscription?.status === 'cancelled'
                                        ? (i18n.language === 'id' ? 'Dibatalkan' : 'Cancelled')
                                        : t('settings.active')}
                                    </span>
                                  </p>
                                </div>
                                {subscription?.status === 'cancelled' ? (
                                  <span className="px-3.5 py-1.5 bg-neutral-900 border border-neutral-800 rounded-xl text-[10px] font-black text-neutral-500 uppercase tracking-widest font-mono">
                                    {i18n.language === 'id' ? 'Perpanjangan Non-Aktif' : 'Auto-Renew Off'}
                                  </span>
                                ) : (
                                  <span className="px-3.5 py-1.5 bg-red-600/10 border border-red-600/30 rounded-xl text-[10px] font-black text-red-500 uppercase tracking-widest">
                                    {t('settings.autoRenewing')}
                                  </span>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-4 border-t border-neutral-900/80 pt-5">
                                <div>
                                  <p className="text-[9px] font-black uppercase tracking-widest text-neutral-500">{t('settings.billingCycle')}</p>
                                  <p className="text-xs font-bold text-white mt-0.5">{t('settings.monthly')}</p>
                                </div>
                                <div>
                                  <p className="text-[9px] font-black uppercase tracking-widest text-neutral-500">
                                    {subscription?.status === 'cancelled'
                                      ? (i18n.language === 'id' ? 'AKSES HINGGA' : 'ACCESS UNTIL')
                                      : t('settings.nextBillingDate')}
                                  </p>
                                  <p className="text-xs font-bold text-white mt-0.5 font-mono">
                                    {subscription?.current_period_end 
                                      ? new Date(subscription.current_period_end).toLocaleDateString(i18n.language === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })
                                      : new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toLocaleDateString(i18n.language === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  </p>
                                </div>
                              </div>

                              {subscription && (subscription.plan === 'pro' || subscription.plan === 'premium') && subscription.status !== 'cancelled' && (
                                <div className="mt-6 pt-5 border-t border-neutral-900/80 flex justify-end">
                                  <button
                                    onClick={() => setCancelFlowStep('reason')}
                                    className="px-4 py-2 rounded-xl border border-neutral-800 hover:border-red-600/30 text-neutral-400 hover:text-red-500 hover:bg-red-500/5 text-[10px] font-black uppercase tracking-widest transition-all active:scale-[0.98] outline-none"
                                  >
                                    {t('settings.cancelSubscription')}
                                  </button>
                                </div>
                              )}
                            </div>

                            {effectivePlan !== 'premium' && (
                              <div className="bg-[#090909] border border-neutral-900 rounded-2xl p-5 space-y-4">
                                <h4 className="text-sm font-black text-white uppercase tracking-tight">{t('settings.upgradeAvailable')}</h4>
                                <p className="text-xs text-neutral-500 leading-relaxed">{t('settings.upgradeHelp')}</p>
                                <button 
                                  onClick={() => {
                                    onClose();
                                    window.location.href = '/pricing';
                                  }}
                                  className="w-full py-3 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-[0.98]"
                                >
                                  {t('settings.explorePricing')}
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {cancelFlowStep === 'reason' && (
                          <div className="space-y-5">
                            <div>
                              <h3 className="text-base font-black text-white tracking-tight">{t('settings.surveyTitle')}</h3>
                            </div>

                            <div className="space-y-2">
                              {[
                                { value: 'technical_issues', labelKey: 'settings.reasonTechnical' },
                                { value: 'budget', labelKey: 'settings.reasonBudget' },
                                { value: 'switching', labelKey: 'settings.reasonSwitching' },
                                { value: 'break', labelKey: 'settings.reasonBreak' },
                                { value: 'missing_features', labelKey: 'settings.reasonMissingFeatures' },
                                { value: 'other', labelKey: 'settings.reasonOther' }
                              ].map((reason) => {
                                const isSelected = selectedReason === reason.value;
                                return (
                                  <div
                                    key={reason.value}
                                    onClick={() => setSelectedReason(reason.value)}
                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                                      isSelected
                                        ? 'bg-red-950/10 border-red-500/50 text-white font-bold'
                                        : 'bg-[#111] border-neutral-900 hover:border-neutral-800 text-neutral-400 hover:text-neutral-200'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                                        isSelected ? 'border-red-500 bg-red-600/20' : 'border-neutral-700 bg-transparent'
                                      }`}>
                                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                                      </div>
                                      <span className="text-xs font-semibold">{t(reason.labelKey)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="flex gap-3 pt-2">
                              <button
                                onClick={() => {
                                  if (referralCodeUsed) {
                                    setCancelFlowStep('confirm_skip');
                                  } else {
                                    setCancelFlowStep('offer');
                                  }
                                }}
                                disabled={!selectedReason}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 border border-transparent disabled:border-neutral-800 disabled:text-neutral-600 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all active:scale-[0.98] outline-none flex items-center justify-center"
                              >
                                {i18n.language === 'id' ? 'Lanjutkan' : 'Continue'}
                              </button>
                              <button
                                onClick={() => setCancelFlowStep('idle')}
                                className="px-5 py-3 bg-neutral-950 border border-neutral-900 hover:border-neutral-800 text-neutral-400 hover:text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none"
                              >
                                {t('settings.backBtn')}
                              </button>
                            </div>
                          </div>
                        )}

                        {cancelFlowStep === 'offer' && (
                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-6 relative overflow-hidden flex flex-col items-center text-center space-y-6">
                            {/* Glowing backdrops */}
                            <div className="absolute -top-12 -left-12 w-40 h-40 bg-red-600 rounded-full blur-[90px] opacity-10" />
                            <div className="absolute -bottom-12 -right-12 w-40 h-40 bg-red-600 rounded-full blur-[90px] opacity-10" />

                            <div className="w-14 h-14 rounded-full bg-red-600/10 border border-red-600/30 flex items-center justify-center text-red-500">
                              <Gift size={28} />
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-lg font-black text-white tracking-tight uppercase">{t('settings.offerTitle')}</h3>
                              <p className="text-xs text-neutral-400 max-w-md mx-auto leading-relaxed">
                                {t(getOfferTranslationKey(selectedReason))}
                              </p>
                            </div>

                            {/* Big Discount Value Display */}
                            <div className="bg-[#111] border border-neutral-800 rounded-2xl px-6 py-4 flex flex-col items-center relative min-w-[200px]">
                              <span className="text-[10px] font-black uppercase tracking-widest text-neutral-500">EXCLUSIVE DISCOUNT</span>
                              <span className="text-4xl font-black text-red-500 mt-1 font-sans">
                                {selectedReason === 'budget' ? '25%' : selectedReason === 'break' ? '50%' : '30%'} OFF
                              </span>
                              <span className="text-[10px] font-bold text-neutral-400 mt-1">
                                for the next {selectedReason === 'budget' ? 4 : selectedReason === 'break' ? 2 : 3} months
                              </span>
                            </div>

                            {cancelError && (
                              <div className="w-full p-3 bg-red-950/20 border border-red-950/50 rounded-xl text-xs text-red-400 font-semibold">
                                {cancelError}
                              </div>
                            )}

                            {/* Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 w-full pt-4">
                              <button
                                onClick={handleApplyDiscount}
                                disabled={isCancelLoading}
                                className="flex-1 py-3 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-[0.98] outline-none flex items-center justify-center gap-2"
                              >
                                {isCancelLoading ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
                                {t('settings.offerApplyBtn')}
                              </button>
                              
                              <button
                                onClick={() => setCancelFlowStep('confirm_skip')}
                                disabled={isCancelLoading}
                                className="flex-1 py-3 bg-neutral-950 border border-neutral-900 hover:border-neutral-800 text-neutral-400 hover:text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none"
                              >
                                {t('settings.skipBtn')}
                              </button>
                            </div>
                            
                            <button
                              onClick={() => setCancelFlowStep('reason')}
                              disabled={isCancelLoading}
                              className="text-[10px] text-neutral-500 hover:text-white font-black uppercase tracking-widest outline-none flex items-center gap-1.5 transition-all"
                            >
                              <ArrowLeft size={10} />
                              {t('settings.backBtn')}
                            </button>
                          </div>
                        )}

                        {cancelFlowStep === 'confirm_skip' && (
                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-6 relative overflow-hidden flex flex-col items-center text-center space-y-5">
                            <div className="absolute -top-12 -left-12 w-40 h-40 bg-amber-600 rounded-full blur-[90px] opacity-10" />

                            <div className="w-14 h-14 rounded-full bg-amber-600/10 border border-amber-600/30 flex items-center justify-center text-amber-500">
                              <AlertTriangle size={28} />
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-lg font-black text-white tracking-tight uppercase">{t('settings.confirmSkipTitle')}</h3>
                              <p className="text-xs text-neutral-400 max-w-md mx-auto leading-relaxed">
                                {t('settings.confirmSkipMessage')}
                              </p>
                            </div>

                            {cancelError && (
                              <div className="w-full p-3 bg-red-950/20 border border-red-950/50 rounded-xl text-xs text-red-400 font-semibold">
                                {cancelError}
                              </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 w-full pt-4">
                              <button
                                onClick={handleCancelSubscription}
                                disabled={isCancelLoading}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-[0.98] outline-none flex items-center justify-center gap-2"
                              >
                                {isCancelLoading && <Loader2 size={14} className="animate-spin" />}
                                {t('settings.confirmSkipBtn')}
                              </button>
                              
                              <button
                                onClick={() => setCancelFlowStep('offer')}
                                disabled={isCancelLoading}
                                className="flex-1 py-3 bg-neutral-950 border border-neutral-900 hover:border-neutral-800 text-neutral-400 hover:text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none"
                              >
                                {t('settings.backBtn')}
                              </button>
                            </div>
                          </div>
                        )}

                        {cancelFlowStep === 'success' && (
                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-6 relative overflow-hidden flex flex-col items-center text-center space-y-5">
                            <div className="absolute -top-12 -left-12 w-40 h-40 bg-green-600 rounded-full blur-[90px] opacity-10" />

                            <div className="w-14 h-14 rounded-full bg-green-600/10 border border-green-600/30 flex items-center justify-center text-green-500">
                              <Check size={28} />
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-lg font-black text-white tracking-tight uppercase">{t('settings.cancelSuccessTitle')}</h3>
                              <p className="text-xs text-neutral-400 max-w-md mx-auto leading-relaxed">
                                {t('settings.cancelSuccessDesc')}
                              </p>
                            </div>

                            <div className="w-full pt-4">
                              <button
                                onClick={() => setCancelFlowStep('idle')}
                                className="w-full py-3 bg-neutral-900 hover:bg-neutral-850 border border-neutral-800 hover:border-neutral-700 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none flex items-center justify-center"
                              >
                                {t('settings.done')}
                              </button>
                            </div>
                          </div>
                        )}

                        {cancelFlowStep === 'waiting_payment' && (
                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-6 relative overflow-hidden flex flex-col items-center text-center space-y-5">
                            <div className="absolute -top-12 -left-12 w-40 h-40 bg-red-600 rounded-full blur-[90px] opacity-10" />

                            <div className="relative">
                              <div className="w-14 h-14 rounded-full bg-red-600/10 border border-red-600/30 flex items-center justify-center text-red-500">
                                <Loader2 size={28} className="animate-spin" />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-lg font-black text-white tracking-tight uppercase">Menunggu Pembayaran</h3>
                              <p className="text-xs text-neutral-400 max-w-md mx-auto leading-relaxed">
                                Silakan selesaikan pembayaran Anda di tab baru yang telah dibuka. Halaman ini akan otomatis diperbarui setelah pembayaran berhasil dideteksi.
                              </p>
                            </div>

                            <div className="w-full pt-4 flex gap-3">
                              <button
                                onClick={async () => {
                                  setIsCancelLoading(true);
                                  if (refreshSubscription) {
                                    await refreshSubscription();
                                  }
                                  setIsCancelLoading(false);
                                }}
                                disabled={isCancelLoading}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 border border-transparent disabled:border-neutral-850 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none flex items-center justify-center gap-2"
                              >
                                {isCancelLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                                Cek Status Manual
                              </button>
                              <button
                                onClick={() => setCancelFlowStep('offer')}
                                className="px-5 py-3 bg-neutral-950 border border-neutral-900 hover:border-neutral-800 text-neutral-400 hover:text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none"
                              >
                                Kembali
                              </button>
                            </div>
                          </div>
                        )}

                        {cancelFlowStep === 'payment_success' && (
                          <div className="bg-neutral-900/30 border border-neutral-900 rounded-2xl p-6 relative overflow-hidden flex flex-col items-center text-center space-y-5">
                            <div className="absolute -top-12 -left-12 w-40 h-40 bg-green-600 rounded-full blur-[90px] opacity-10" />

                            <div className="w-14 h-14 rounded-full bg-green-600/10 border border-green-600/30 flex items-center justify-center text-green-500 animate-bounce">
                              <Check size={28} />
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-lg font-black text-white tracking-tight uppercase">Pembayaran Berhasil!</h3>
                              <p className="text-xs text-neutral-400 max-w-md mx-auto leading-relaxed">
                                Terima kasih! Penawaran diskon Anda telah berhasil diterapkan dan langganan Anda kini aktif kembali dengan harga promo.
                              </p>
                            </div>

                            <div className="w-full pt-4">
                              <button
                                onClick={() => {
                                  setCancelFlowStep('idle');
                                  onClose();
                                }}
                                className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-650 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all outline-none flex items-center justify-center"
                              >
                                Mulai Trading
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* TAB: HISTORY LOG */}
                    {activeTab === 'history' && (
                      <motion.div 
                        key="history" 
                        initial={{ opacity: 0, x: 10 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-4"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Activity size={14} className="text-red-500 animate-pulse" />
                          <p className="text-[10px] text-neutral-500 font-black uppercase tracking-widest">{t('settings.activityLogsHeader')}</p>
                        </div>
                        
                        <div className="bg-[#111] border border-neutral-800 rounded-2xl overflow-hidden">
                          <table className="w-full text-left border-collapse">
                            <thead className="bg-[#0A0A0A] text-[9px] uppercase font-black text-neutral-500 tracking-widest border-b border-neutral-800">
                              <tr>
                                <th className="px-5 py-3">{t('settings.activityAction')}</th>
                                <th className="px-5 py-3 text-right">{t('settings.activityTime')}</th>
                                <th className="px-5 py-3 text-right">{t('settings.activityIp')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-900 font-mono text-[10px]">
                              {logs.map(log => (
                                <tr key={log.id} className="hover:bg-neutral-900/40 text-neutral-300">
                                  <td className="px-5 py-3 font-sans font-bold text-white">
                                    {log.action.startsWith('settings.log') ? t(log.action) : log.action}
                                  </td>
                                  <td className="px-5 py-3 text-right text-neutral-400 font-semibold">{log.time}</td>
                                  <td className="px-5 py-3 text-right text-neutral-500 font-semibold">{log.ip}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}

                    {/* TAB: WALLETS */}
                    {activeTab === 'wallets' && (
                      <WalletsTab onOpenAddWallet={() => setShowAddWallet(true)} onOpenAddNetwork={(addr, nets) => { setAddNetworkAddress(addr); setAddNetworkTracked(nets); setShowAddWallet(true); }} />
                    )}

                  </AnimatePresence>
                </div>
                
                {/* Mobile logout button */}
                <div className="p-4 border-t border-neutral-900 sm:hidden shrink-0 bg-[#090909]">
                  <button 
                    onClick={logout}
                    className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-red-950/20 hover:bg-red-950/30 text-red-500 text-xs font-black uppercase tracking-widest border border-red-500/20"
                  >
                    <LogOut size={14} />
                    {t('topbar.logout')}
                  </button>
                </div>

              </div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>

    {/* Wallet modal — rendered outside the main modal stack */}
    <AddWalletModal
      isOpen={showAddWallet}
      onClose={() => { setShowAddWallet(false); setAddNetworkAddress(undefined); setAddNetworkTracked([]); }}
      onSuccess={() => { setShowAddWallet(false); setAddNetworkAddress(undefined); setAddNetworkTracked([]); }}
      prefilledAddress={addNetworkAddress}
      prefilledType={addNetworkAddress ? 'evm' : undefined}
      alreadyTrackedNetworks={addNetworkTracked}
    />
    </>
  );
};

// ── WalletsTab subcomponent ──────────────────────────────────────────────────
interface WalletsTabProps {
  onOpenAddWallet: () => void;
  onOpenAddNetwork: (address: string, alreadyTracked: string[]) => void;
}

const WalletsTab: React.FC<WalletsTabProps> = ({ onOpenAddWallet, onOpenAddNetwork }) => {
  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const { walletAccounts, fetchWalletAccounts, isWalletLoading } = useCexAccount();
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // Group wallets by address
  const grouped = React.useMemo(() => {
    const map = new Map<string, typeof walletAccounts>();
    for (const w of walletAccounts) {
      const key = w.wallet_address.toLowerCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(w);
    }
    return Array.from(map.entries());
  }, [walletAccounts]);

  const handleSync = async (id: number) => {
    setSyncingId(id);
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_BASE_URL}/api/wallet-accounts/${id}/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchWalletAccounts();
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_BASE_URL}/api/wallet-accounts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchWalletAccounts();
    } finally {
      setDeletingId(null);
    }
  };

  const getNetworkIcon = (n: string) => SUPPORTED_WALLET_NETWORKS.find(w => w.value === n)?.icon;
  const getNetworkColor = (n: string) => SUPPORTED_WALLET_NETWORKS.find(w => w.value === n)?.color || '#666';

  return (
    <motion.div
      key="wallets"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      className="space-y-4"
    >
      {/* Info banner */}
      <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl px-4 py-3 flex gap-3">
        <Info size={15} className="text-neutral-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-neutral-500 leading-relaxed">
          {isId ? t('settings.walletManageNetworksBanner') : t('settings.walletManageNetworksBanner')}
        </p>
      </div>

      {/* Empty state */}
      {walletAccounts.length === 0 && !isWalletLoading && (
        <div className="text-center py-10 space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-neutral-800 border border-neutral-700 flex items-center justify-center mx-auto">
            <Wallet size={24} className="text-neutral-500" />
          </div>
          <p className="text-white font-bold text-sm">{t('settings.walletsEmpty')}</p>
          <p className="text-neutral-500 text-xs max-w-xs mx-auto">{t('settings.walletsEmptyDesc')}</p>
          <button
            onClick={onOpenAddWallet}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-xl transition-colors mt-2"
          >
            <Plus size={16} />
            {t('settings.addFirstWallet')}
          </button>
        </div>
      )}

      {/* Grouped wallet list */}
      {grouped.map(([address, wallets]) => {
        const firstWallet = wallets[0];
        const trackedNetworks = wallets.map(w => w.network_name);
        // EVM address = starts with 0x
        const isEVM = address.startsWith('0x');

        return (
          <div key={address} className="bg-neutral-900/40 border border-neutral-800 rounded-2xl overflow-hidden">
            {/* Address header */}
            <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] text-neutral-600 uppercase tracking-widest mb-1 font-bold">
                  {firstWallet.account_label || (isEVM ? 'EVM Wallet' : firstWallet.network_display_name)}
                </p>
                <p className="text-sm font-mono text-neutral-300 truncate">{address}</p>
              </div>
              {/* Add network button — only for EVM (same address works across chains) */}
              {isEVM && (
                <button
                  onClick={() => onOpenAddNetwork(address, trackedNetworks)}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 rounded-lg text-xs font-bold text-neutral-300 hover:text-white transition-colors"
                >
                  <Plus size={12} />
                  {t('settings.walletAddNetwork')}
                </button>
              )}
            </div>

            {/* Per-network rows */}
            {wallets.map(wallet => (
              <div key={wallet.id} className="flex items-center gap-3 px-5 py-3 border-b border-neutral-800/60 last:border-0 hover:bg-neutral-900/40 transition-colors group">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${getNetworkColor(wallet.network_name)}20` }}
                >
                  {getNetworkIcon(wallet.network_name)
                    ? <img src={getNetworkIcon(wallet.network_name)} alt={wallet.network_display_name} className="w-5 h-5 object-contain" />
                    : <Wallet size={14} style={{ color: getNetworkColor(wallet.network_name) }} />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">{wallet.network_display_name}</p>
                  <p className="text-xs text-neutral-500">
                    {wallet.last_synced_at
                      ? `${t('settings.walletLastSync')}: ${new Date(wallet.last_synced_at).toLocaleDateString()}`
                      : t('settings.walletNeverSynced')
                    }
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleSync(wallet.id)}
                    disabled={syncingId === wallet.id}
                    className="p-1.5 rounded-lg hover:bg-neutral-800 transition-colors"
                    title={t('settings.walletSyncNow')}
                  >
                    <RefreshCw size={13} className={`text-neutral-400 hover:text-white ${syncingId === wallet.id ? 'animate-spin' : ''}`} />
                  </button>

                  {confirmDeleteId === wallet.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(wallet.id)}
                        className="text-[10px] px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded font-bold"
                      >Yes</button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-[10px] px-1.5 py-0.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded font-bold"
                      >No</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(wallet.id)}
                      disabled={deletingId === wallet.id}
                      className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
                      title={t('settings.walletRemove')}
                    >
                      {deletingId === wallet.id
                        ? <Loader2 size={13} className="text-red-400 animate-spin" />
                        : <Trash2 size={13} className="text-neutral-400 hover:text-red-400" />
                      }
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* Add wallet button */}
      {walletAccounts.length > 0 && (
        <button
          onClick={onOpenAddWallet}
          className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-neutral-700 hover:border-neutral-500 rounded-xl text-sm font-medium text-neutral-500 hover:text-white transition-colors"
        >
          <Plus size={16} />
          {t('addWallet.addManually')}
        </button>
      )}
    </motion.div>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold transition-all text-left whitespace-nowrap shrink-0 sm:w-full border outline-none ${
        active 
          ? 'bg-red-700/10 border-red-600/30 text-red-500' 
          : 'bg-transparent border-transparent text-neutral-500 hover:text-white hover:bg-neutral-900'
      }`}
    >
      <span className={active ? 'text-red-500' : 'text-neutral-500'}>{icon}</span>
      <span>{label}</span>
    </button>
  );
};
